import {
  Colors,
  IconPack,
  Multimedia,
  Palette,
  PaletteType,
  TypographyClass,
  TypographyConfiguration,
} from "../../../gen/witbooking/backoffice/common/common_pb";
import { PropertyConfigurationAPIPromiseClient } from "../../../gen/witbooking/backoffice/propertyconfiguration/v1/propertyconfiguration_api_grpc_web_pb";
import {
  EngineAppearance,
  GetPropertyConfigurationRequest,
  UpdateEngineAppearanceRequest,
} from "../../../gen/witbooking/backoffice/propertyconfiguration/v1/propertyconfiguration_api_pb";
import { buildGrpcClient } from "../../../lib/grpc";
import {
  EngineAppearanceEntity,
  SimpleEngineAppearanceEntity,
} from "../domain/engineAppearance.entity";
import { Image } from "../../shared/widgets/ImageManager";

const propertyconfigurationClient: PropertyConfigurationAPIPromiseClient = buildGrpcClient(
  PropertyConfigurationAPIPromiseClient
);

function oppositeColorHex(rgbaColor: string): string {
  const rgbaRegex = /rgba\((\d+),\s*(\d+),\s*(\d+),\s*\d+\)/;
  const match = rgbaColor.match(rgbaRegex);
  if (!match) {
    throw new Error("Invalid RGBA format");
  }

  const [, r, g, b] = match;
  const red = parseInt(r, 10);
  const green = parseInt(g, 10);
  const blue = parseInt(b, 10);

  return red + green + blue > 255 * 1.5 ? "rgba(0, 0, 0, 1)" : "rgba(255, 255, 255, 1)";
}

function colorsFromObject(colors: Colors.AsObject | undefined): Colors {
  if (!colors) {
    throw new Error("Function not implemented.");
  }
  const result = new Colors();
  result.setLight(colors.light);
  result.setMain(colors.main);
  result.setDark(colors.dark);
  result.setContrasttext(colors.contrasttext);
  return result;
}

function paletteFromObject(palette: Palette.AsObject | undefined): Palette {
  if (!palette) {
    throw new Error("Function not implemented.");
  }
  const result = new Palette();
  result.setId(palette.id);
  result.setTicker(palette.ticker);
  result.setType(palette.type);
  result.setPrimary(colorsFromObject(palette.primary));
  result.setSecondary(colorsFromObject(palette.secondary));
  result.setError(colorsFromObject(palette.error));
  result.setSuccess(colorsFromObject(palette.success));
  return result;
}
function typographyClassFromObject(obj: TypographyClass.AsObject | undefined): TypographyClass {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new TypographyClass();
  result.setFontsize(obj.fontsize);
  result.setFontweight(obj.fontweight);
  result.setFontfamily(obj.fontfamily);
  result.setLineheight(obj.lineheight);
  result.setFontstyle(obj.fontstyle);
  result.setLetterspacing(obj.letterspacing);
  return result;
}
function typographyConfigurationFromObject(
  obj: TypographyConfiguration.AsObject | undefined
): TypographyConfiguration {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new TypographyConfiguration();
  result.setFontsize(obj.fontsize);
  result.setFontweightlight(obj.fontweightlight);
  result.setFontweightregular(obj.fontweightregular);
  result.setFontweightmedium(obj.fontweightmedium);
  result.setFontweightbold(obj.fontweightbold);
  result.setHtmlfontsize(obj.htmlfontsize);
  result.setFontfamily(obj.fontfamily);
  result.setH1(typographyClassFromObject(obj.h1));
  result.setH2(typographyClassFromObject(obj.h2));
  result.setH3(typographyClassFromObject(obj.h3));
  result.setH4(typographyClassFromObject(obj.h4));
  result.setH5(typographyClassFromObject(obj.h5));
  result.setH6(typographyClassFromObject(obj.h6));
  result.setSubtitle1(typographyClassFromObject(obj.subtitle1));
  result.setSubtitle2(typographyClassFromObject(obj.subtitle2));
  result.setSubtitle3(typographyClassFromObject(obj.subtitle3));
  result.setBody1(typographyClassFromObject(obj.body1));
  result.setBody2(typographyClassFromObject(obj.body2));
  result.setBody3(typographyClassFromObject(obj.body3));
  result.setButton(typographyClassFromObject(obj.button));
  result.setCaption(typographyClassFromObject(obj.caption));
  result.setOverline(typographyClassFromObject(obj.overline));
  return result;
}
function iconPackFromObject(obj: IconPack.AsObject | undefined): IconPack {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new IconPack();
  result.setTicker(obj.ticker);
  result.setName(obj.name);
  return result;
}
function multimediaFromObject(obj: Multimedia.AsObject | undefined): Multimedia {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new Multimedia();
  result.setId(obj.id);
  result.setFilename(obj.filename);
  result.setUrl(obj.url);
  return result;
}
function engineAppearanceFromObject(obj: EngineAppearanceEntity): EngineAppearance {
  const result = new EngineAppearance();
  result.setDarkmode(obj.darkmode);
  result.setPalette(paletteFromObject(obj.palette));
  result.setTypographyconfiguration(typographyConfigurationFromObject(obj.typographyconfiguration));
  result.setMobiletypographyconfiguration(
    typographyConfigurationFromObject(obj.mobiletypographyconfiguration)
  );
  result.setIconpack(iconPackFromObject(obj.iconpack));
  result.setAccommodationresultsformattype(obj.accommodationresultsformattype);
  result.setInventoryresultsformattype(obj.inventoryresultsformattype);
  result.setDisplaymode(obj.displaymode);
  result.setBookingadvantagesposition(obj.bookingadvantagesposition);
  result.setBookingadvantagesloyaltymodule(obj.bookingadvantagesloyaltymodule);
  result.setButtonroundtype(obj.buttonroundtype);
  result.setTagroundtype(obj.tagroundtype);
  result.setBookingformroundtype(obj.bookingformroundtype);
  result.setCardroundtype(obj.cardroundtype);
  result.setFormroundtype(obj.formroundtype);
  result.setBookingformborder(obj.bookingformborder);
  result.setBookingadvantagesicon(obj.bookingadvantagesicon);
  result.setHeadercolor(colorsFromObject(obj.headercolor));
  result.setBodycolor(colorsFromObject(obj.bodycolor));
  result.setButtoncolor(colorsFromObject(obj.buttoncolor));
  result.setTagcolor(colorsFromObject(obj.tagcolor));
  result.setBookingadvantagescolor(colorsFromObject(obj.bookingadvantagescolor));
  result.setLogo(multimediaFromObject(obj.logo));
  result.setMobilelogo(multimediaFromObject(obj.mobilelogo));
  result.setEmaillogo(multimediaFromObject(obj.emaillogo));
  result.setFavicon(multimediaFromObject(obj.favicon));
  return result;
}

export async function getEngineAppearance(token: string): Promise<EngineAppearanceEntity> {
  const request = new GetPropertyConfigurationRequest();
  const response = await propertyconfigurationClient.getPropertyConfiguration(request, {
    Authorization: `Bearer ${token}`,
  });
  const engineAppearance = response.getConfiguration()?.toObject().engineappearance;
  if (!engineAppearance) {
    throw new Error("Unexpected reply obtaining engineAppearance");
  }
  return { ...engineAppearance, id: "engineAppearance" };
}
function imageFromMultimedia(multimedia: Multimedia.AsObject): Image[] {
  if (multimedia.filename === "empty.png") {
    return [];
  }
  return [
    Object.assign(new File([""], multimedia.filename), {
      id: multimedia.id,
      url: multimedia.url,
      filename: multimedia.filename,
    }),
  ];
}
export async function getSimpleEngineAppearance(
  token: string
): Promise<SimpleEngineAppearanceEntity> {
  const engineAppearanceEntity = await getEngineAppearance(token);
  return {
    id: engineAppearanceEntity.id,
    primaryColor: engineAppearanceEntity.palette?.primary?.main ?? "rgba(0, 143, 105, 1)",
    secondaryColor: engineAppearanceEntity.palette?.secondary?.main ?? "rgba(0, 143, 105, 1)",
    logo: engineAppearanceEntity.logo ? imageFromMultimedia(engineAppearanceEntity.logo) : [],
    mobilelogo: engineAppearanceEntity.mobilelogo
      ? imageFromMultimedia(engineAppearanceEntity.mobilelogo)
      : [],
    emaillogo: engineAppearanceEntity.emaillogo
      ? imageFromMultimedia(engineAppearanceEntity.emaillogo)
      : [],
    favicon: engineAppearanceEntity.favicon
      ? imageFromMultimedia(engineAppearanceEntity.favicon)
      : [],
  };
}
export async function updateEngineAppearance(
  entity: EngineAppearanceEntity,
  token: string
): Promise<void> {
  const request = new UpdateEngineAppearanceRequest();
  const engineAppearanceEntity = engineAppearanceFromObject(entity);
  request.setEngineappearance(engineAppearanceEntity);
  await propertyconfigurationClient.updateEngineAppearance(request, {
    Authorization: `Bearer ${token}`,
  });
}
// we do not need the palette inside the engineAppearance to have a distinct UUID
const baseUUID = "3639076e-8f86-442f-b1c6-d123b718b9c7";
export async function updateSimpleEngineAppearance(
  entity: SimpleEngineAppearanceEntity,
  token: string
): Promise<void> {
  const engineAppearanceEntity = await getEngineAppearance(token);
  const newEntity: EngineAppearanceEntity = {
    ...engineAppearanceEntity,
    palette: {
      ...engineAppearanceEntity.palette,
      id: baseUUID,
      ticker: "mainPalette",
      type: PaletteType.PUBLIC,
      primary: {
        main: entity.primaryColor,
        light: engineAppearanceEntity.palette?.primary?.light ?? entity.primaryColor,
        dark: engineAppearanceEntity.palette?.primary?.dark ?? entity.primaryColor,
        contrasttext: oppositeColorHex(entity.primaryColor),
      },
      secondary: {
        main: entity.secondaryColor,
        light: engineAppearanceEntity.palette?.secondary?.light ?? entity.secondaryColor,
        dark: engineAppearanceEntity.palette?.secondary?.dark ?? entity.secondaryColor,
        contrasttext: oppositeColorHex(entity.secondaryColor),
      },
    },
    logo: engineAppearanceEntity.logo,
    mobilelogo: engineAppearanceEntity.mobilelogo,
    emaillogo: engineAppearanceEntity.emaillogo,
    favicon: engineAppearanceEntity.favicon,
  };
  await updateEngineAppearance(newEntity, token);
}
